import { SnackbarOrigin } from '@mui/material'
import { AlertColor } from '@mui/material/Alert/Alert'
import { C1SnackBar } from 'components/core/snackbar/C1Snackbar'
import { createContext, useEffect, useRef, useState } from 'react'

type alertMessage = React.ReactElement | string

export type AlertBarProviderProps = {
    children: alertMessage
    maxAlerts: number
}

export type alertStateType = {
    message: alertMessage
    severity: AlertColor
    open: boolean
    key: string
    duration?: number
    anchorOrigin?: SnackbarOrigin
}
export type alerter = (message: alertMessage, severity: AlertColor, duration?: number, anchorOrigin?: SnackbarOrigin) => void
export type AlertBarProviderContext = alerter

export const AlertBarContext = createContext<AlertBarProviderContext>(() => undefined)

// AlertBarProvider does not currently work with more than one alert at a time.
const AlertBarProvider = ({ children, maxAlerts }: AlertBarProviderProps): React.ReactElement => {
    const index = useRef<number>(0)
    const [alerter, setAlerter] = useState<alerter>((): void => undefined)
    const [alerts, setAlerts] = useState<alertStateType[]>([])

    useEffect(() => {
        setAlerter(() => (message: alertMessage, severity: AlertColor, duration = 3000, anchorOrigin?: SnackbarOrigin): void => {
            const key = `alert-state${index.current}`
            const alertStateInstance: alertStateType = {
                key,
                message,
                severity,
                open: true,
                duration,
                anchorOrigin,
            }
            if (alerts.length < maxAlerts - 1) {
                setAlerts(alerts.concat(alertStateInstance))
            } else {
                if (alerts[index.current]?.open) {
                    // There's a current alert showing for this index. Use an updated key to reset the timer on the snackbar.
                    alertStateInstance.key += `${alerts[index.current].key}_` // Just append a _ to the existing key.
                }
                alerts[index.current] = alertStateInstance
                setAlerts(([] as alertStateType[]).concat(alerts))
            }
            index.current += 1
            if (index.current >= maxAlerts) {
                index.current = 0
            }
        })
        // We don't need to update the alerter function whenever the deps change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const closeHandler = (idx: number) => (): void => {
        alerts[idx].open = false
        setAlerts(([] as alertStateType[]).concat(alerts))
    }
    const renderedAlerts = alerts.map((state: alertStateType, idx: number) => (
        <C1SnackBar
            open={state.open}
            severity={state.severity}
            key={state.key}
            onClose={closeHandler(idx)}
            autoHideDuration={
                state.duration === 0
                    ? null
                    : (state.duration !== undefined
                        ? state.duration
                        : 3000)}
            anchorOrigin={state.anchorOrigin}
        >
            <>{state.message}</>
        </C1SnackBar>
    ))
    return (
        <AlertBarContext.Provider value={alerter}>
            {renderedAlerts}
            {children}
        </AlertBarContext.Provider>
    )
}
export default AlertBarProvider
